import type { DeviceGenre } from "@/constants/device";
// デバイス棚卸しパッケージの型定義
// APIスキーマから型をインポート
import type {
  ApiRequestBody,
  ApiResponseBody,
  ApiSearchQuery,
} from "@/lib/api/api-type-helper";

import type {
  DeviceInventoryDisplayStatus,
  DeviceInventoryInstance,
  DeviceTaskStatus,
} from "./query/type";

// デバイス状態の型定義
export type DeviceCondition = "GOOD" | "BAD";
export type DeviceStatus = DeviceCondition | "UNREPORTED";

// レポート提出状態の型定義
export type ReportStatus = "REPORTED" | "NOT_REPORTED";

// レポート結果の型定義（API値を参考に定義）
export type PlacementVerificationStatus = "OWNED" | "LOST" | "NOT_KNOWN";

// デバイス棚卸し専用の検証方法型定義（API返り値から定義）
export type VerificationMethod =
  | "SCAN" // QRコードスキャン
  | "ASSET_TAG" // アセットタグ手動入力
  | "REPRESENTATIVE_BY_STAFF"; // スタッフによる代行確認

// レポート代行時専用の検証方法型定義（ASSET_TAGを除外）
export type ReportProxyVerificationMethod = Exclude<
  VerificationMethod,
  "ASSET_TAG"
>;

// 検証方法の定数
export const VERIFICATION_METHOD = {
  SCAN: "SCAN",
  ASSET_TAG: "ASSET_TAG",
  REPRESENTATIVE_BY_STAFF: "REPRESENTATIVE_BY_STAFF",
} as const satisfies Record<string, VerificationMethod>;

// 配置確認状態の定数
export const PLACEMENT_VERIFICATION_STATUS = {
  OWNED: "OWNED",
  LOST: "LOST",
  NOT_KNOWN: "NOT_KNOWN",
} as const satisfies Record<string, PlacementVerificationStatus>;

// デバイス状態の定数
export const DEVICE_CONDITION = {
  GOOD: "GOOD",
  BAD: "BAD",
} as const satisfies Record<string, DeviceCondition>;

// レポート状態の定数
export const REPORT_STATUS = {
  REPORTED: "REPORTED",
  NOT_REPORTED: "NOT_REPORTED",
} as const satisfies Record<string, ReportStatus>;

/**
 * デバイス棚卸しフォームのフィールドID定数
 * フォーム構造: Collection.Index.Group.Index.Attribute
 */
export const DEVICE_INVENTORY_FIELD_IDS = {
  // デバイスID情報セクション
  DEVICE_ID: "DeviceIDInformation.0.DeviceIDInformation.0.DeviceID",
  DEVICE_TAG: "DeviceIDInformation.0.DeviceIDInformation.0.DeviceTag",

  // デバイス配置・使用状況セクション
  VERIFICATION_METHOD:
    "DeviceUseAndPlacement.0.DevicePlacementStatus.0.VerificationMethod",
  PLACEMENT_VERIFICATION_STATUS:
    "DeviceUseAndPlacement.0.DevicePlacementStatus.0.PlacementVerificationStatus",
  INVENTORY_DEVICE_CONDITION:
    "DeviceUseAndPlacement.0.DevicePlacementStatus.0.InventoryDeviceCondition",
  INVENTORY_REPORTER:
    "DeviceUseAndPlacement.0.DevicePlacementStatus.0.InventoryReporter",
  INVENTORY_MEMO:
    "DeviceUseAndPlacement.0.DevicePlacementStatus.0.InventoryMemo",
} as const;

// APIタスクタイプの定義
export type ApiStepType =
  | "セルフレポートの依頼"
  | "セルフレポート"
  | "セルフレポート結果";

// APIタスクタイプの定数
export const API_STEP_TYPES = {
  SELF_REPORT_REQUEST: "セルフレポートの依頼" as const,
  SELF_REPORT: "セルフレポート" as const,
  REVIEW_RESULTS: "セルフレポート結果" as const,
} as const;

// フロントエンドで使用するタスクタイプのキー
export type StepTypeKey = "selfReportRequest" | "selfReport" | "reviewResults";

// APIタスクタイプとフロントエンドキーのマッピング
export const STEP_TYPE_MAPPING: Record<ApiStepType, StepTypeKey> = {
  [API_STEP_TYPES.SELF_REPORT_REQUEST]: "selfReportRequest",
  [API_STEP_TYPES.SELF_REPORT]: "selfReport",
  [API_STEP_TYPES.REVIEW_RESULTS]: "reviewResults",
} as const;

// 逆マッピング（フロントエンドキーからAPIタスクタイプへ）
export const REVERSE_TASK_TYPE_MAPPING: Record<StepTypeKey, ApiStepType> = {
  selfReportRequest: API_STEP_TYPES.SELF_REPORT_REQUEST,
  selfReport: API_STEP_TYPES.SELF_REPORT,
  reviewResults: API_STEP_TYPES.REVIEW_RESULTS,
} as const;

export type DeviceAssigneeType = "assigned" | "installed"; // 貸与 or 設置

export interface DeviceInventorySummary {
  totalDeviceCount: number;
  assigningDeviceCount: number;
  installationDeviceCount: number;
  responsiblePersonCount: number;
}

export interface DeviceInventoryAssignee {
  id: string;
  name: string;
  avatarUrl?: string;
}

export interface DeviceInventorySelfReportResult {
  reportedAt: string;
  reportedBy: string;
  deviceCondition: DeviceStatus;
  reportResult?: PlacementVerificationStatus;
  memo?: string;
}

export interface DeviceInventoryDevice {
  packageInstanceId: string;
  taskInstanceId: string;
  displayStatus: DeviceInventoryDisplayStatus;
  taskStatus: DeviceTaskStatus;
  deviceId: string;
  deviceName: string;
  deviceTag?: string;
  deviceModel: string | null;
  deviceImageUrl?: string;
  deviceGenre: DeviceGenre;
  assigneeType: DeviceAssigneeType;
  assignee?: DeviceInventoryAssignee;
  responsiblePerson?: DeviceInventoryAssignee;
  owner?: DeviceInventoryAssignee;
  status: DeviceStatus;
  reportStatus: ReportStatus; // レポート提出状態
  selfReportResult?: DeviceInventorySelfReportResult;
  lastUpdatedAt: string;
}

// DeviceInventoryInstanceはAPIスキーマから使用

// デバイスタスクの型定義（InstanceDetailResponseのtasksから）
export interface DeviceTask {
  id: string;
  name: string;
  taskCode: string;
  progressStatus:
    | "Pending"
    | "InProgress"
    | "Completed"
    | "WaitingApproval"
    | "Abort";
  assignee?: DeviceInventoryAssignee;
}

// テーブル表示用のデータ構造
export interface DeviceInventoryDataRow {
  // anchorDateグループの場合
  anchorDate?: string;
  requestId?: string;
  children?: DeviceInventoryInstance[]; // 元のインスタンス配列
  patternId?: string; // anchorDateグループ行の詳細画面遷移用

  // デバイス行の場合
  device: {
    deviceId: string;
    deviceName: string;
    deviceTag?: string;
    deviceImageUrl?: string;
  };

  // このデバイスの進捗
  progress: {
    completedTaskCount: number;
    totalTaskCount: number;
  };

  // 各Stepのステータス
  stepStatuses: Record<StepTypeKey, DeviceTask | null>;

  // デバイスの状態
  status: DeviceStatus;

  // 担当者情報
  assignee?: DeviceInventoryAssignee;
  responsiblePerson?: DeviceInventoryAssignee;
  owner?: DeviceInventoryAssignee;

  // 元のインスタンスデータ
  instanceId: string;
  createdAt: string;
  updatedAt: string;
}

// API型定義（実際のAPIに移行しやすい形）
// StartDeviceInventoryRequest/ResponseはAPIスキーマから使用
export type StartDeviceInventoryRequest = ApiRequestBody<
  "/v2/it-operation-core/device-inventory",
  "post"
>;
export type StartDeviceInventoryResponse = ApiResponseBody<
  "/v2/it-operation-core/device-inventory",
  "post"
>;

// ListInstancesQueryはAPIスキーマから使用

// ListInstancesResponseはAPIスキーマから使用

// インスタンス詳細API（新しいAPI）
export type InstancesDetailQuery = ApiSearchQuery<
  "/v2/it-operation-core/device-inventory/{patternId}/detail",
  "get"
>;
export type InstancesDetailResponse = ApiResponseBody<
  "/v2/it-operation-core/device-inventory/{patternId}/detail",
  "get"
>;

export type GetProgressResponse = ApiResponseBody<
  "/v2/it-operation-core/device-inventory/{requestId}/progress",
  "get"
>;

export interface DeviceInventoryTasksQuery {
  page?: number;
  limit?: number;
  status?: DeviceStatus;
  assigneeType?: DeviceAssigneeType;
}

export interface DeviceInventoryTasksResponse {
  items: DeviceInventoryDevice[];
  total: number;
  page: number;
  limit: number;
  summary: DeviceInventorySummary;
}

export interface TaskCountResponse {
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  byStatus: Record<DeviceStatus, number>;
}

export interface ReportProxyRequest {
  deviceId: string;
  taskInstanceId: string;
  packageInstanceId: string;
  verificationMethod: ReportProxyVerificationMethod;
  placementVerificationStatus: PlacementVerificationStatus;
  deviceCondition: DeviceCondition;
  reportedBy: string;
  memo?: string;
  deviceTag: string;
  // レスポンス構築用の追加情報
  deviceName?: string;
  deviceImageUrl?: string;
}

export interface ReportProxyResponse {
  success: boolean;
  updatedDevice: DeviceInventoryDevice;
}

// Bulk Complete Tasks API 型定義
// Use API type helpers to ensure type consistency with backend
export type BulkCompleteTasksRequest = ApiRequestBody<
  "/v2/it-operation-core/operation-instances/tasks/bulk-complete",
  "post"
>;

export type BulkCompleteTasksResponse = ApiResponseBody<
  "/v2/it-operation-core/operation-instances/tasks/bulk-complete",
  "post"
>;

// TaskItem型もAPIから直接取得
export type TaskItem = ApiRequestBody<
  "/v2/it-operation-core/operation-instances/tasks/bulk-complete",
  "post"
>["tasks"][number];

// セルフレポート代行処理 API 型定義
export interface SaveFormOperationPackageInstanceTaskParams {
  /** Operation Package InstanceのID */
  instanceId: string;
  /** Task ID */
  taskId: string;
}

export interface SaveFormOperationPackageInstanceTaskPayload {
  /** フォームデータ（キーは動的なフィールド名） */
  [key: string]: string | File | undefined;
}

export interface SaveFormOperationPackageInstanceTaskResponse {
  /** 空のレスポンス（成功時） */
}
